/**
 * Which Deliveries rows each bulk (multi-select) action can take.
 *
 * CR-7420 gave the grid one bulk action, "Appeal Selected". CR-8214 adds
 * "Acknowledge Selected" for the customer, so a row is now selectable when
 * either action applies to it, and each button acts on its own subset.
 */
import type { GoodsReceiptNote } from '../../../../lib/api/types/grn-types';
import { countsAsOnTime } from '../../../../lib/onTimeStatus';  // CR-6518

/**
 * Whether a delivery can join a bulk "delivered on time" appeal: a pending,
 * early/late GRN that can be appealed and has no open or decided appeal
 * (mirrors the single-appeal gavel's visibility).
 *
 * @param grn - the delivery row
 * @returns true when the bulk appeal would accept it
 */
export function isBulkAppealEligible(grn: GoodsReceiptNote): boolean {
  return grn.workflow_status === 'pending_review'
    && !countsAsOnTime(grn.on_time_status)  // CR-6518: an accepted override is not an exception
    && grn.can_appeal === true
    && !(grn.latest_appeal && ['awaiting_response', 'accepted', 'rejected'].includes(grn.latest_appeal.status));
}

/**
 * CR-8214: whether a delivery can join a bulk acknowledgement — exactly the
 * rows that show the customer's per-row Acknowledge tick in `DeliveryRow`.
 * An overridden verdict shows no controls (CR-6517), and a supplier appeal
 * awaiting the customer's answer shows Accept / Query / Reject instead, so
 * both are left out. The server applies the same rule
 * (`bulk_acknowledge_skip_reason`).
 *
 * @param grn - the delivery row
 * @returns true when the bulk acknowledge would accept it
 */
export function isBulkAcknowledgeEligible(grn: GoodsReceiptNote): boolean {
  const appeal = grn.latest_appeal;
  return !grn.otif_overridden
    && grn.workflow_status === 'pending_review'
    && !countsAsOnTime(grn.on_time_status)
    && !(appeal && ['accepted', 'rejected'].includes(appeal.status))
    && !(appeal && appeal.status === 'awaiting_response' && !appeal.is_customer_appeal);
}
